import styled from '@emotion/styled';
import { Headset, IconContext, MoneyWavy, Smiley } from '@phosphor-icons/react';
import { Box, Flex, Image, Text, theme, useTheme } from '@updater/ui-uds';
import { BorderRadiusKey } from '@updater/ui-uds/dist/es/types/theme-types';
import { useAddress, useStore, useAcceptUpdaterTerms } from 'hooks';
import { ValidatedAddress } from 'hooks/address';
import { SingleLineAddressInput } from 'components/SingleLineAddressInput';
import { TermsAndPrivacyDisclaimer } from 'components/TermsAndPrivacyDisclaimer';
import { useRouter } from 'next/router';
import { useCallback, useContext } from 'react';
import { Events, useEnvironment, usePageEvent } from 'services';
import { PageRoutes } from 'constants/pageRoutes';
import { ResponsiveValue, Theme } from 'styled-system';
import type { MicrositeCustomTheme, MicrositeTheme } from 'types';
import { useShallow } from 'zustand/react/shallow';
import { MicrositeContext } from 'context';
import { Palette } from '@updater/ui-uds';

const ValueListItem = styled(Box)`
  height: 144px;
  padding: ${({ theme }) => `${theme.space.s}px`};
  background-color: ${({ theme }) => theme.customTheme.baseColors.cta}1F;
  border-radius: ${({ theme }) => theme.button.borderRadii.m};
  svg {
    margin: 0 auto;
    display: block;
  }
`;

const CtaSection: React.FC<{
  onSubmit: (address: ValidatedAddress) => void;
}> = ({ onSubmit }) => {
  const { smartyStreetsAPIKey } = useEnvironment();
  const initialAddress = useAddress();
  return (
    <Flex
      width="100%"
      maxWidth="620px"
      marginTop="none"
      marginBottom="l"
      marginX="auto"
      flexDirection="column"
      gap={`${theme.space.s}px`}
    >
      <SingleLineAddressInput
        smartyStreetsAPIKey={smartyStreetsAPIKey}
        onComplete={onSubmit}
        initialAddress={initialAddress}
      >
        <SingleLineAddressInput.Typeahead />
        <SingleLineAddressInput.UnitField />
        <SingleLineAddressInput.ContinueButton label="See all plans" />
      </SingleLineAddressInput>

      <TermsAndPrivacyDisclaimer mt="s" textAlign="center" />
    </Flex>
  );
};

export function LandingPageContent() {
  const router = useRouter();
  const acceptUpdaterTerms = useAcceptUpdaterTerms();
  const [setAddress] = useStore(useShallow((store) => [store.setAddress]));
  const theme = useTheme();
  const { displayName = 'Updater' } = useContext(MicrositeContext);

  // Single tracking event with partner info
  usePageEvent(Events.LANDING);

  const { customTheme } = theme as unknown as MicrositeCustomTheme &
    MicrositeTheme;

  const setAddressAndRouteToQuiz = useCallback(
    ({ validation: _v, ...address }: ValidatedAddress) => {
      setAddress(address);
      acceptUpdaterTerms(address);
      router.push(PageRoutes.quiz);
    },
    [router, setAddress, acceptUpdaterTerms]
  );

  return (
    <Box width="100%" maxWidth="940px" marginY="l" marginX="auto">
      <Box
        width="100%"
        maxWidth="620px"
        marginY="none"
        marginX="auto"
        borderBottom={`1px solid ${Palette.gray400}`}
      >
        <Box
          width="100%"
          height={['200px', '300px', '300px', '300px', '300px']}
          backgroundColor="softBlue"
          borderRadius={
            theme.button.borderRadii.m as ResponsiveValue<
              BorderRadiusKey,
              Theme
            >
          }
        >
          <Image
            borderRadius={
              theme.button.borderRadii.m as ResponsiveValue<
                BorderRadiusKey,
                Theme
              >
            }
            width="100%"
            height="100%"
            marginY="none"
            marginX="auto"
            display="block"
            objectFit="cover"
            src="https://res.cloudinary.com/updater-staging/image/upload/f_auto,q_auto:best,dpr_auto,fl_progressive,h_900/tv-internet/family_hero"
            alt="A family enjoying fast broadband internet access in their home."
          />
        </Box>
        <Text
          as="h1"
          variant={['xlBold', 'xlBold', 'xxlBold', 'xxlBold', 'xxlBold']}
          marginY="s"
          textAlign="center"
        >
          Get connected with {displayName}
        </Text>
        <Text
          as="p"
          variant={['s', 'm', 'l', 'l', 'l']}
          textAlign="center"
          mb="xs"
        >
          Enter your new home address to compare the best plans for your home.
        </Text>
        <CtaSection onSubmit={setAddressAndRouteToQuiz} />
      </Box>

      <Box
        maxWidth={['100%', '620px', '620px', '940px', '940px']}
        marginX="auto"
        borderTop="1px solid"
        borderBottom="1px solid"
        borderColor="border"
        py="2.25rem"
      >
        <Text as="h2" variant="xlBold" textAlign="center">
          Wherever you live, we've got you covered
        </Text>

        <Box
          marginTop="m"
          mx="auto"
          maxWidth={['100%', '300px', '450px', '450px', '450px']}
        >
          <Flex justifyContent="space-between">
            <Image
              height={['2rem', '2rem', '2rem', '2.5rem', '2.5rem']}
              src="https://res.cloudinary.com/updater-staging/image/upload/f_auto,q_auto,dpr_auto,fl_progressive,h_40/tv-internet/provider-logos/full-color/xfinity"
              alt="xfinity logo"
            />
            <Image
              height={['2rem', '2rem', '2rem', '2.5rem', '2.5rem']}
              src="https://res.cloudinary.com/updater-staging/image/upload/f_auto,q_auto,dpr_auto,fl_progressive,h_40/tv-internet/provider-logos/full-color/att"
              alt="att logo"
            />
            <Image
              height={['2rem', '2rem', '2rem', '2.5rem', '2.5rem']}
              src="https://res.cloudinary.com/updater-staging/image/upload/f_auto,q_auto,dpr_auto,fl_progressive,h_40/tv-internet/provider-logos/full-color/cox"
              alt="cox logo"
            />
          </Flex>
          <Flex justifyContent="space-around" mt="1rem">
            <Image
              height={['2rem', '2rem', '2rem', '2.5rem', '2.5rem']}
              src="https://res.cloudinary.com/updater-staging/image/upload/f_auto,q_auto,dpr_auto,fl_progressive,h_40/tv-internet/provider-logos/full-color/spectrum"
              alt="spectrum logo"
            />
            <Flex
              height={['2rem', '2rem', '2rem', '2.5rem', '2.5rem']}
              width="5rem"
            >
              <Text as="span" variant={['m', 'm', 'l', 'l', 'l']} my="auto">
                + more
              </Text>
            </Flex>
          </Flex>
        </Box>
      </Box>

      <Box
        mt="2.25rem"
        maxWidth={['100%', '620px', '620px', '940px', '940px']}
        marginX="auto"
      >
        <Text as="h2" variant="xlBold" textAlign="center">
          Why shop with {displayName}?
        </Text>
        <Flex
          marginTop="m"
          width="100%"
          gap={`${theme.space.s}px`}
          flexWrap="wrap"
          justifyContent="space-between"
        >
          <IconContext.Provider
            value={{
              color: customTheme?.baseColors.cta,
              size: '32px',
              weight: 'light',
            }}
          >
            <ValueListItem width={['100%', '100%', '100%', '302px', '302px']}>
              <Smiley />
              <Text as="h3" variant="mBold" marginY="xxs" textAlign="center">
                Save time & sanity
              </Text>
              <Text as="p" variant="s" marginY="xxs" textAlign="center">
                Compare internet plans like flights - all at once.
              </Text>
            </ValueListItem>
            <ValueListItem width={['100%', '100%', '100%', '302px', '302px']}>
              <MoneyWavy />
              <Text as="h3" variant="mBold" marginY="xxs" textAlign="center">
                We search - you save
              </Text>
              <Text as="p" variant="s" marginY="xxs" textAlign="center">
                No added fees or markups, ever.
              </Text>
            </ValueListItem>
            <ValueListItem width={['100%', '100%', '100%', '302px', '302px']}>
              <Headset />
              <Text as="h3" variant="mBold" marginY="xxs" textAlign="center">
                Get live advice from a real human
              </Text>
              <Text as="p" variant="s" marginY="xxs" textAlign="center">
                Instant support from our team of expert concierges.
              </Text>
            </ValueListItem>
          </IconContext.Provider>
        </Flex>
      </Box>
    </Box>
  );
}
